import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTechSpecPage() {
  const navigate = useNavigate();
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/technical-spec`, {
          // axios will attach the bearer token via its default interceptor (set in AuthContext)
          responseType: 'text',
        });
        if (!cancelled) {
          setHtml(typeof data === 'string' ? data : String(data));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 403 || err.response?.status === 401) {
            setError('Admin access required to view this document.');
          } else {
            setError('Failed to load the technical specification. Please try again.');
          }
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <PageLayout showNav={false}>
      <div className="px-4 py-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/settings')}
            data-testid="tech-spec-back-btn"
            className="text-text-secondary hover:text-primary"
            aria-label="Back to Settings"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <FileText className="w-6 h-6 text-primary" />
          <h1 className="font-heading font-bold text-dark text-2xl">Technical Specification</h1>
        </div>

        {loading && (
          <div className="card-default p-12 text-center" data-testid="tech-spec-loading">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
            <p className="text-text-secondary text-sm">Loading confidential specification…</p>
          </div>
        )}

        {error && !loading && (
          <div className="card-default p-8 text-center border-l-4 border-alert" data-testid="tech-spec-error">
            <AlertCircle className="w-10 h-10 text-alert mx-auto mb-3" />
            <p className="font-semibold text-dark mb-1">Access denied</p>
            <p className="text-sm text-text-secondary mb-4">{error}</p>
            <Button onClick={() => navigate('/settings')} className="btn-secondary px-4 py-2">
              Back to Settings
            </Button>
          </div>
        )}

        {!loading && !error && html && (
          <div
            className="card-default p-0 overflow-hidden bg-white"
            data-testid="tech-spec-content"
          >
            <iframe
              title="Technical Specification"
              srcDoc={html}
              sandbox="allow-same-origin"
              className="w-full border-0"
              style={{ minHeight: '85vh', height: '85vh' }}
              data-testid="tech-spec-iframe"
            />
          </div>
        )}

        {!loading && !error && (
          <p className="text-xs text-text-light text-center mt-4">
            This document is confidential and is only accessible to authorised admin accounts.
          </p>
        )}
      </div>
    </PageLayout>
  );
}
